import React, { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { formatDays, leaveDays, toDayKey, toLeaveWindow } from "../core/leaveDates";

/**
 * "Apply for leave" (Claude Design "Profile Modal Redesign" 4a).
 *
 * Leave types come from the backend. Once both dates are picked the coach's
 * own bookings in that window are counted; any hit shows a red warning and
 * the submit button becomes "Apply anyway" — applying is still allowed, since
 * nothing is cancelled until a manager approves.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Array<{ id: string, label: string, hint: string }>} props.leaveTypes
 * @param {(window: { start: string, end: string }) => Promise<number>} props.countBookings
 * @param {(window: { start: string, end: string }) => string|null} [props.overlapFor]
 *   - A sentence when the dates clash with one of the coach's own open
 *   leaves; shown as soon as the dates are picked, and submit is blocked.
 * @param {(fields: { leaveTypeId: string, start: string, end: string, reason: string }) => Promise<void>} props.onSubmit
 *   - Rejects with an Error whose message is shown in the form.
 */
const ApplyLeaveModal = ({ isOpen, onClose, leaveTypes, countBookings, overlapFor, onSubmit }) => {
  const [typeId, setTypeId] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [fromDay, setFromDay] = useState("");
  const [toDay, setToDay] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bookings, setBookings] = useState({ status: "idle", count: 0 });
  const requestRef = useRef(0);

  // Fresh form every time it opens.
  useEffect(() => {
    if (!isOpen) return;
    setTypeId("");
    setMenuOpen(false);
    setFromDay("");
    setToDay("");
    setReason("");
    setError("");
    setSubmitting(false);
    setBookings({ status: "idle", count: 0 });
  }, [isOpen]);

  const leaveWindow = toLeaveWindow(fromDay, toDay);
  const windowKey = leaveWindow ? `${leaveWindow.start}|${leaveWindow.end}` : "";

  useEffect(() => {
    if (!isOpen || !leaveWindow) {
      setBookings({ status: "idle", count: 0 });
      return;
    }
    const request = ++requestRef.current;
    setBookings({ status: "loading", count: 0 });
    countBookings(leaveWindow)
      .then((count) => {
        if (requestRef.current === request) setBookings({ status: "done", count });
      })
      .catch(() => {
        if (requestRef.current === request) setBookings({ status: "error", count: 0 });
      });
  }, [isOpen, windowKey]);

  if (!isOpen) return null;

  const today = toDayKey(new Date());
  const selectedType = leaveTypes.find((t) => t.id === typeId);
  const days = leaveWindow ? leaveDays({ start_date: leaveWindow.start, end_date: leaveWindow.end }) : 0;
  const overlap = leaveWindow && overlapFor ? overlapFor(leaveWindow) : null;
  // Overlapping another leave can't be applied anyway, so the bookings
  // warning (and its "Apply anyway") would only mislead.
  const hasClash = !overlap && bookings.status === "done" && bookings.count > 0;

  const handleSubmit = async () => {
    if (!typeId) return setError("Choose a leave type.");
    if (!fromDay) return setError("Choose a start date.");
    if (!toDay) return setError("Choose an end date.");
    if (toDay < fromDay) return setError("End date must be on or after the start date.");
    if (overlap) return undefined;
    setError("");
    setSubmitting(true);
    try {
      await onSubmit({ leaveTypeId: typeId, start: leaveWindow.start, end: leaveWindow.end, reason: reason.trim() });
    } catch (err) {
      setError(err?.message || "Couldn't submit your leave request. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[rgba(31,35,40,0.4)] flex items-center justify-center p-4"
      onClick={submitting ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="apply-leave-title"
        onClick={(e) => {
          e.stopPropagation();
          if (menuOpen && !e.target.closest("[data-leave-type-menu]")) setMenuOpen(false);
        }}
        className="w-full max-w-[440px] max-h-[90vh] bg-white rounded-[14px] shadow-[0_24px_60px_rgba(31,35,40,0.28)] flex flex-col text-[#1F2328]"
      >
        <div className="px-[22px] py-[18px] flex items-center border-b border-[#EFEBE7]">
          <h2 id="apply-leave-title" className="flex-1 text-[17px] font-extrabold">
            Apply for leave
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            className="w-8 h-8 rounded-lg bg-[#F4F1EE] text-[#5C554F] flex items-center justify-center hover:bg-[#EFEBE7]"
          >
            <IoClose className="w-4 h-4" />
          </button>
        </div>

        <div className="px-[22px] py-5 flex flex-col gap-4 overflow-y-auto min-h-0">
          <div className="flex flex-col gap-1.5 relative" data-leave-type-menu>
            <span id="leave-type-label" className="text-[13px] font-bold">
              Leave type
            </span>
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={menuOpen}
              aria-labelledby="leave-type-label"
              onClick={() => setMenuOpen((o) => !o)}
              className={`h-[42px] rounded-lg px-3 flex items-center text-sm bg-white text-left border-[1.5px] ${
                menuOpen ? "border-[#F2601F] shadow-[0_0_0_3px_#FFE3D2]" : "border-[#E1DCD6]"
              }`}
            >
              <span className={`flex-1 font-semibold ${selectedType ? "text-[#1F2328]" : "text-[#8A8178]"}`}>
                {selectedType ? selectedType.label : "Select leave type"}
              </span>
              <span className="text-[11px] text-[#8A8178]">{menuOpen ? "▲" : "▼"}</span>
            </button>
            {menuOpen && (
              <div
                role="listbox"
                aria-labelledby="leave-type-label"
                className="absolute top-[72px] left-0 right-0 bg-white border border-[#E8E5E1] rounded-[10px] shadow-[0_12px_32px_rgba(31,35,40,0.14)] p-1 flex flex-col z-10"
              >
                {leaveTypes.length === 0 && (
                  <span className="px-2.5 py-3 text-sm text-[#8A8178]">No leave types available.</span>
                )}
                {leaveTypes.map((t) => {
                  const on = t.id === typeId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="option"
                      aria-selected={on}
                      onClick={() => {
                        setTypeId(t.id);
                        setMenuOpen(false);
                        setError("");
                      }}
                      className={`min-h-[44px] px-2.5 py-1.5 rounded-md text-left flex items-center gap-2 hover:bg-[#FAF8F6] ${
                        on ? "bg-[#FFF6F0]" : ""
                      }`}
                    >
                      <span className="flex-1 flex flex-col">
                        <span className="text-sm font-bold">{t.label}</span>
                        {t.hint && <span className="text-xs text-[#8A8178]">{t.hint}</span>}
                      </span>
                      {on && <span className="text-[#E8590C] font-extrabold">✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-bold">From – To</span>
            <div className="h-[42px] border border-[#E1DCD6] rounded-lg px-3 flex items-center gap-2 focus-within:border-[#F2601F] focus-within:shadow-[0_0_0_3px_#FFE3D2]">
              <input
                type="date"
                aria-label="From"
                min={today}
                value={fromDay}
                onChange={(e) => {
                  setFromDay(e.target.value);
                  if (toDay && e.target.value > toDay) setToDay("");
                  setError("");
                }}
                className="flex-1 min-w-0 border-none text-sm outline-none bg-transparent"
              />
              <span className="text-[#8A8178]">–</span>
              <input
                type="date"
                aria-label="To"
                min={fromDay || today}
                value={toDay}
                onChange={(e) => {
                  setToDay(e.target.value);
                  setError("");
                }}
                className="flex-1 min-w-0 border-none text-sm outline-none bg-transparent"
              />
            </div>
            <span className="text-xs text-[#8A8178]">
              {days ? formatDays(days) : "Pick start and end dates."}
              {bookings.status === "loading" && " · Checking your bookings…"}
            </span>
          </div>

          {overlap && (
            <div
              role="alert"
              className="text-[13px] font-bold text-[#B42318] bg-[#FDECEA] rounded-lg px-3 py-2.5"
            >
              {overlap}
            </div>
          )}
          {hasClash && (
            <div
              role="alert"
              className="text-[13px] font-bold text-[#B42318] bg-[#FDECEA] rounded-lg px-3 py-2.5"
            >
              You have {bookings.count} {bookings.count === 1 ? "booking" : "bookings"} during this
              leave. Do you still want to apply?
            </div>
          )}
          {bookings.status === "error" && (
            <div className="text-xs text-[#8A8178]">Couldn't check your bookings for these dates.</div>
          )}

          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-bold">
              Remarks <span className="font-semibold text-[#8A8178]">· optional</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Shared with your manager"
              className="min-h-[76px] border border-[#E1DCD6] rounded-lg px-3 py-2.5 text-sm resize-y outline-none focus:border-[#F2601F] focus:shadow-[0_0_0_3px_#FFE3D2]"
            />
          </label>

          {error && (
            <div role="alert" className="text-[13px] font-bold text-[#B42318] bg-[#FDECEA] rounded-lg px-3 py-2.5">
              {error}
            </div>
          )}
        </div>

        <div className="px-[22px] py-[14px] border-t border-[#EFEBE7] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-[38px] px-4 rounded-lg border border-[#E8E5E1] bg-white text-sm font-bold hover:bg-[#FAF8F6]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || Boolean(overlap)}
            className={`h-[38px] px-[18px] rounded-lg text-white text-sm font-bold ${
              typeId && !overlap ? "bg-[#F2601F] hover:bg-[#E8590C]" : "bg-[#F6B48F]"
            } disabled:opacity-70 disabled:cursor-not-allowed`}
          >
            {submitting ? "Submitting…" : hasClash ? "Apply anyway" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplyLeaveModal;
